import { useEffect, useState } from "react";
import { CheckCheck, Download, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, ts } from "@/lib/api";
import { EdiTag } from "@/components/EdiTag";
import { PageHeader, Panel, Th, Td, Empty, btn } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { RawEdiDialog } from "@/components/RawEdiDialog";

const FILTERS = [["pending", "To review"], ["confirmed", "Confirmed"], ["flagged", "Flagged"], ["", "All"]];

const download = async (m) => {
  const r = await api.get(`/edi/messages/${m.id}`);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([r.data.raw], { type: "text/plain" }));
  a.download = `${m.doc_type}_${m.control || m.id}.edi`;
  a.click();
};

export default function Outputs() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState("pending");
  const [view, setView] = useState(null);
  const load = () => api.get("/outputs", { params: { review: f || undefined } }).then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  const review = async (m, decision) => {
    try {
      await api.post(`/edi/messages/${m.id}/review`, { decision, note: "" });
      toast.success(decision === "confirmed" ? "Output confirmed" : "Output flagged");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const confirmAll = async () => {
    const r = await api.post("/outputs/confirm-all");
    toast.success(`${r.data.confirmed} output(s) confirmed`);
    load();
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Output review" subtitle="Every file the system generates for Group O (940, 850, 997). Open each one, check the segments, download it, then confirm it is correct or flag it.">
        {f === "pending" && rows.length > 0 && <Button onClick={confirmAll} className={btn.primary} data-testid="outputs-confirm-all-btn"><CheckCheck className="mr-1.5 h-4 w-4" /> Confirm all</Button>}
      </PageHeader>
      <div className="flex flex-wrap gap-1.5" data-testid="outputs-filter">
        {FILTERS.map(([v, l]) => (
          <button key={v || "all"} onClick={() => setF(v)} data-testid={`outputs-filter-${v || "all"}`}
            className={`rounded-sm border px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${f === v ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-slate-800 text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <Panel>
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="outputs-table">
              <thead className="border-b border-slate-800"><tr><Th>Generated</Th><Th>Type</Th><Th>Control</Th><Th>Summary</Th><Th>Transmission</Th><Th>Review</Th><Th></Th></tr></thead>
              <tbody className="divide-y divide-slate-800/70">
                {rows.map((m) => (
                  <tr key={m.id} data-testid={`output-row-${m.id}`}>
                    <Td className="whitespace-nowrap font-mono text-slate-500">{ts(m.created_at)}</Td>
                    <Td className="whitespace-nowrap font-mono text-amber-300"><EdiTag code={m.doc_type} />{m.test && <span className="ml-2 rounded-sm bg-amber-500/20 px-1 text-[9px] text-amber-200">TEST</span>}</Td>
                    <Td className="font-mono">{m.control || "—"}</Td>
                    <Td className="text-slate-200">{m.summary}</Td>
                    <Td><StatusBadge status={m.status} /></Td>
                    <Td><StatusBadge status={`review_${m.review || "pending"}`} testId={`output-review-${m.id}`} /></Td>
                    <Td className="whitespace-nowrap text-right">
                      <Button size="sm" variant="ghost" onClick={() => setView(m.id)} className={btn.ghost} data-testid={`output-view-${m.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => download(m)} className={`ml-1 ${btn.ghost}`} data-testid={`output-download-${m.id}`}><Download className="h-3.5 w-3.5" /></Button>
                      {m.review !== "confirmed" && <Button size="sm" onClick={() => review(m, "confirmed")} className={`ml-1 ${btn.primary}`} data-testid={`output-confirm-${m.id}`}>Confirm</Button>}
                      {m.review !== "flagged" && <Button size="sm" variant="ghost" onClick={() => review(m, "flagged")} className={`ml-1 ${btn.danger}`} data-testid={`output-flag-${m.id}`}>Flag</Button>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty testId="outputs-empty">Nothing here.</Empty>}
      </Panel>
      <RawEdiDialog open={!!view} messageId={view} onOpenChange={(v) => !v && setView(null)} onChanged={load} />
    </div>
  );
}
